import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  CONTRACT_KIND_LABEL,
  alertBadgeVariant,
  daysUntil,
  formatDate,
  formatYen,
  type Contract,
  type ContractKind,
} from '../components/api'
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  FormField,
  Select,
} from '../components/ui'

type StatusFilter = '' | 'active' | 'ended'
type SortKey = 'expires' | 'amount_desc' | 'amount_asc'

export function ContractListPage() {
  const [items, setItems] = useState<Contract[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [kind, setKind] = useState<'' | ContractKind>('')
  const [status, setStatus] = useState<StatusFilter>('')
  const [sort, setSort] = useState<SortKey>('expires')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<{ items: Contract[] }>('/api/contracts', {
      query: { active: status === 'active' ? 1 : undefined },
    })
      .then((d) => {
        if (!cancelled) setItems(d.items ?? [])
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [status])

  const filtered = useMemo(() => {
    let arr = items
    if (kind) arr = arr.filter((c) => c.kind === kind)
    if (status === 'ended') arr = arr.filter((c) => c.ended_at !== null)
    const sorted = [...arr]
    if (sort === 'expires') {
      sorted.sort((a, b) => {
        const da = a.ended_at ? new Date(a.ended_at).getTime() : Infinity
        const db = b.ended_at ? new Date(b.ended_at).getTime() : Infinity
        return da - db
      })
    } else if (sort === 'amount_desc') {
      sorted.sort((a, b) => b.monthly_amount - a.monthly_amount)
    } else {
      sorted.sort((a, b) => a.monthly_amount - b.monthly_amount)
    }
    return sorted
  }, [items, kind, status, sort])

  const mrr = useMemo(
    () =>
      items
        .filter((c) => c.ended_at === null)
        .reduce((sum, c) => sum + (c.monthly_amount ?? 0), 0),
    [items],
  )

  return (
    <>
      <div className="page-header">
        <h1>保守契約</h1>
        <div className="row" style={{ gap: 8 }}>
          <Badge>
            稼働月額合計：<strong>{formatYen(mrr)}</strong>
          </Badge>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <div className="form-row">
          <FormField label="状態" htmlFor="status">
            <Select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="">すべて</option>
              <option value="active">稼働中</option>
              <option value="ended">終了</option>
            </Select>
          </FormField>
          <FormField label="種別" htmlFor="kind">
            <Select
              id="kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as '' | ContractKind)}
            >
              <option value="">すべて</option>
              {(Object.keys(CONTRACT_KIND_LABEL) as ContractKind[]).map((k) => (
                <option key={k} value={k}>
                  {CONTRACT_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="並び順" htmlFor="sort">
            <Select
              id="sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              <option value="expires">終了日が近い順</option>
              <option value="amount_desc">月額が高い順</option>
              <option value="amount_asc">月額が安い順</option>
            </Select>
          </FormField>
        </div>
      </Card>

      <Card>
        {loading ? (
          <p className="muted">読み込み中…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="該当する契約がありません" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>顧客</th>
                <th>種別</th>
                <th className="text-right">月額</th>
                <th>開始日</th>
                <th>終了日</th>
                <th>自動更新</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const active = c.ended_at === null
                const days = daysUntil(c.ended_at)
                return (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/customers/${c.customer_id}`}>
                        {c.customer_name ?? '—'}
                      </Link>
                    </td>
                    <td>{CONTRACT_KIND_LABEL[c.kind]}</td>
                    <td className="text-right">
                      {formatYen(c.monthly_amount)}
                    </td>
                    <td>{formatDate(c.started_at)}</td>
                    <td>
                      {formatDate(c.ended_at)}
                      {days !== null && active && (
                        <Badge variant={alertBadgeVariant(days)}>
                          {days >= 0 ? `あと${days}日` : `${-days}日経過`}
                        </Badge>
                      )}
                    </td>
                    <td>{c.auto_renew ? '有効' : '—'}</td>
                    <td>
                      <Badge variant={active ? 'success' : 'muted'}>
                        {active ? '稼働中' : '終了'}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
